import React, { useState } from "react";
import { ChevronDown, ChevronRight, Building2, Users } from "lucide-react";
import { Badge } from "../ui/badge";

const SubsidiaryHierarchyTree = ({ subsidiaries = [] }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Build hierarchy structure
  const buildHierarchy = () => {
    const subsidiaryMap = new Map();
    const rootNodes = [];

    // Create a map of all subsidiaries
    subsidiaries.forEach(sub => {
      subsidiaryMap.set(sub.id, { ...sub, children: [] });
    });

    // Build parent-child relationships
    subsidiaries.forEach(sub => {
      if (sub.parent_id) {
        const parent = subsidiaryMap.get(sub.parent_id);
        if (parent) {
          parent.children.push(subsidiaryMap.get(sub.id));
        } else {
          // If parent not found, treat as root
          rootNodes.push(subsidiaryMap.get(sub.id));
        }
      } else {
        rootNodes.push(subsidiaryMap.get(sub.id));
      }
    });

    return rootNodes;
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    const allIds = new Set(subsidiaries.map(s => s.id));
    setExpandedNodes(allIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const TreeNode = ({ node, level = 0 }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const paddingLeft = level * 24;

    return (
      <div>
        <div
          className="flex items-center py-2 px-3 hover:bg-gray-50 rounded cursor-pointer group"
          style={{ paddingLeft: `${paddingLeft + 12}px` }}
        >
          {/* Expand/Collapse Icon */}
          <div className="w-6 flex items-center justify-center">
            {hasChildren ? (
              <button
                onClick={() => toggleNode(node.id)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-600" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                )}
              </button>
            ) : (
              <div className="w-4 h-4" />
            )}
          </div>

          {/* Building Icon */}
          <Building2 className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />

          {/* Subsidiary Name */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 truncate">
                {node.name}
              </span>
              {node.code && (
                <Badge variant="outline" className="text-xs">
                  {node.code}
                </Badge>
              )}
            </div>
            {node.location && (
              <p className="text-xs text-gray-500 truncate">{node.location}</p>
            )}
          </div>

          {/* Team Count */}
          <div className="flex items-center gap-1 ml-2">
            <Users className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">{node.team_count || 0}</span>
          </div>

          {/* Status Badge */}
          <div className="ml-3">
            <Badge
              variant={node.is_active !== false ? "default" : "secondary"}
              className={
                node.is_active !== false
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-700"
              }
            >
              {node.is_active !== false ? "Active" : "Inactive"}
            </Badge>
          </div>
        </div>

        {/* Render Children */}
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child) => (
              <TreeNode key={child.id} node={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const hierarchy = buildHierarchy();

  if (subsidiaries.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No subsidiaries available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Control Buttons */}
      <div className="flex items-center justify-between border-b pb-3">
        <div className="text-sm text-gray-500">
          Showing {subsidiaries.length} subsidiaries
        </div>
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Expand All
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={collapseAll}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Tree View */}
      <div className="border rounded-lg">
        {hierarchy.map((node) => (
          <TreeNode key={node.id} node={node} level={0} />
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500 pt-2">
        <div className="flex items-center gap-1">
          <Building2 className="h-4 w-4" />
          <span>Subsidiary</span>
        </div>
        <div className="flex items-center gap-1">
          <Users className="h-4 w-4" />
          <span>Team Members</span>
        </div>
        <div className="flex items-center gap-1">
          <ChevronRight className="h-4 w-4" />
          <span>Expand/Collapse</span>
        </div>
      </div>
    </div>
  );
};

export default SubsidiaryHierarchyTree;
